import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer, ComposedChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, ReferenceLine, ReferenceDot, Legend,
} from 'recharts';
import { ArrowLeft, Play, Pause, SkipBack, SkipForward, TrendingUp, TrendingDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API_V2 = `${process.env.REACT_APP_BACKEND_URL}/api/v2`;

const PAIRS = ['BTC-USD', 'ETH-USD', 'SOL-USD', 'LTC-USD', 'DOGE-USD', 'BCH-USD', 'XRP-USD', 'SHIB-USD'];
const TIMEFRAMES = [
  { label: '1H', minutes: 60 },
  { label: '6H', minutes: 360 },
  { label: '24H', minutes: 1440 },
  { label: '7D', minutes: 10080 },
  { label: '30D', minutes: 43200 },
];

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtPrice = (v) => {
  if (v === null || v === undefined || isNaN(v)) return '—';
  if (v < 0.01) return `$${Number(v).toFixed(8)}`;
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export default function TradeTimelinePage() {
  const navigate = useNavigate();
  const [pair, setPair] = useState('BTC-USD');
  const [minutes, setMinutes] = useState(1440);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ candles: [], trades: [], mode: 'simulation' });
  const [playheadIdx, setPlayheadIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const intervalRef = useRef(null);

  const fetchTimeline = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_V2}/user/bot/trade-timeline`, {
        params: { pair, minutes },
        withCredentials: true,
      });
      setData({
        candles: res.data.candles || [],
        trades: res.data.trades || [],
        mode: res.data.mode || 'simulation',
        start_ts: res.data.start_ts,
        end_ts: res.data.end_ts,
      });
      setPlayheadIdx(Math.max(0, (res.data.candles || []).length - 1));
    } catch (err) {
      console.error('[TradeTimeline] fetch failed', err);
      setData({ candles: [], trades: [], mode: 'simulation' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline();
    setPlaying(false);
     
  }, [pair, minutes]);

  // Play/pause auto-advance
  useEffect(() => {
    if (!playing) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setPlayheadIdx((idx) => {
        if (idx >= data.candles.length - 1) {
          setPlaying(false);
          return idx;
        }
        return idx + 1;
      });
    }, 100); // 10 fps
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [playing, data.candles.length]);

  // Merge candles + trade markers into a single chart dataset
  const chartData = useMemo(() => {
    if (!data.candles?.length) return [];
    // Index trades by nearest candle
    const candleTimes = data.candles.map((c) => new Date(c.timestamp).getTime());
    const merged = data.candles.map((c) => ({
      timestamp: c.timestamp,
      time: new Date(c.timestamp).getTime(),
      price: c.close ?? c.price,
    }));
    (data.trades || []).forEach((t) => {
      const tt = new Date(t.timestamp).getTime();
      // find nearest candle idx (binary search-ish; small N so linear is fine)
      let bestIdx = 0;
      let bestDelta = Infinity;
      for (let i = 0; i < candleTimes.length; i++) {
        const d = Math.abs(candleTimes[i] - tt);
        if (d < bestDelta) {
          bestDelta = d;
          bestIdx = i;
        }
      }
      if (t.type === 'BUY') {
        merged[bestIdx].buy = t.price || merged[bestIdx].price;
        merged[bestIdx].buyTrade = t;
      } else if (t.type === 'SELL') {
        merged[bestIdx].sell = t.price || merged[bestIdx].price;
        merged[bestIdx].sellTrade = t;
      }
    });
    return merged;
  }, [data]);

  // Playhead-visible slice (0..playheadIdx)
  const visibleData = useMemo(() => {
    if (!chartData.length) return [];
    return chartData.slice(0, Math.max(1, playheadIdx + 1));
  }, [chartData, playheadIdx]);

  // Trades revealed up to the playhead
  const revealedTrades = useMemo(() => {
    if (!chartData.length || !data.trades?.length) return [];
    const cutoffTime = chartData[Math.min(playheadIdx, chartData.length - 1)]?.time;
    if (!cutoffTime) return [];
    return data.trades.filter((t) => new Date(t.timestamp).getTime() <= cutoffTime);
  }, [chartData, playheadIdx, data.trades]);

  // Cumulative P/L across revealed sells
  const cumulativeProfit = useMemo(() => {
    return revealedTrades.reduce((sum, t) => sum + (t.type === 'SELL' ? (t.profit || 0) : 0), 0);
  }, [revealedTrades]);

  const buyCount = revealedTrades.filter((t) => t.type === 'BUY').length;
  const sellCount = revealedTrades.filter((t) => t.type === 'SELL').length;

  const currentPoint = visibleData[visibleData.length - 1];
  const priceRange = useMemo(() => {
    if (!chartData.length) return { min: 0, max: 0 };
    let min = Infinity;
    let max = -Infinity;
    chartData.forEach((d) => {
      if (d.price < min) min = d.price;
      if (d.price > max) max = d.price;
    });
    return { min, max };
  }, [chartData]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="bg-black/95 border border-[#00FF41] text-[#00FF41] p-3 text-xs font-mono">
        <div>{fmtTime(d.timestamp)}</div>
        <div>Price: {fmtPrice(d.price)}</div>
        {d.buyTrade && (
          <div className="mt-1 text-green-400">
            BUY {d.buyTrade.amount.toFixed(8)} @ {fmtPrice(d.buyTrade.price)}
            {d.buyTrade.trigger ? ` · ${d.buyTrade.trigger}` : ''}
          </div>
        )}
        {d.sellTrade && (
          <div className="mt-1 text-red-400">
            SELL {d.sellTrade.amount.toFixed(8)} @ {fmtPrice(d.sellTrade.price)}
            {' · P/L '}${d.sellTrade.profit.toFixed(2)}
            {d.sellTrade.trigger ? ` · ${d.sellTrade.trigger}` : ''}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-[#00FF41] font-mono p-3 md:p-6" data-testid="trade-timeline-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[#00FF41] hover:opacity-70 transition-opacity uppercase tracking-widest text-xs md:text-sm"
          data-testid="timeline-back-btn"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          Back to Dashboard
        </button>
        <div className="text-sm md:text-lg font-bold uppercase tracking-widest glow-text" data-testid="timeline-title">
          TRADE TIMELINE
        </div>
      </div>

      {/* Controls */}
      <Card className="matrix-card rounded-none mb-4">
        <CardHeader className="border-b border-[#003B00] p-3 md:p-4">
          <CardTitle className="text-xs uppercase tracking-widest flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              REPLAY · {data.mode?.toUpperCase()} · {data.trades?.length || 0} TRADES
            </div>
            <div className="flex gap-1 flex-wrap">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf.label}
                  onClick={() => setMinutes(tf.minutes)}
                  className={`px-2 py-1 text-xs border ${
                    minutes === tf.minutes
                      ? 'bg-[#00FF41] text-black border-[#00FF41]'
                      : 'border-[#003B00] hover:border-[#00FF41]'
                  }`}
                  data-testid={`timeline-tf-${tf.label}`}
                >
                  {tf.label}
                </button>
              ))}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-3 md:p-4 space-y-3">
          {/* Pair pills */}
          <div className="flex gap-1 flex-wrap">
            {PAIRS.map((p) => (
              <button
                key={p}
                onClick={() => setPair(p)}
                className={`px-2 py-1 text-xs border ${
                  pair === p
                    ? 'bg-[#00FF41] text-black border-[#00FF41]'
                    : 'border-[#003B00] hover:border-[#00FF41]'
                }`}
                data-testid={`timeline-pair-${p}`}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="border border-[#003B00] p-2">
              <div className="opacity-60 uppercase text-[10px]">Current Price</div>
              <div className="text-sm text-[#00FF41]" data-testid="timeline-current-price">
                {fmtPrice(currentPoint?.price)}
              </div>
            </div>
            <div className="border border-[#003B00] p-2">
              <div className="opacity-60 uppercase text-[10px]">Buys Shown</div>
              <div className="text-sm text-green-400" data-testid="timeline-buys">{buyCount}</div>
            </div>
            <div className="border border-[#003B00] p-2">
              <div className="opacity-60 uppercase text-[10px]">Sells Shown</div>
              <div className="text-sm text-red-400" data-testid="timeline-sells">{sellCount}</div>
            </div>
            <div className="border border-[#003B00] p-2">
              <div className="opacity-60 uppercase text-[10px]">Realized P/L</div>
              <div
                className={`text-sm ${cumulativeProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}
                data-testid="timeline-pnl"
              >
                ${cumulativeProfit.toFixed(2)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Chart */}
      <Card className="matrix-card rounded-none mb-4">
        <CardContent className="p-2 md:p-4">
          {loading ? (
            <div className="h-[380px] flex items-center justify-center opacity-60 text-xs uppercase tracking-widest" data-testid="timeline-loading">
              Loading candles…
            </div>
          ) : chartData.length === 0 ? (
            <div className="h-[380px] flex items-center justify-center opacity-60 text-xs uppercase tracking-widest" data-testid="timeline-empty">
              No data for this window
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              <ComposedChart data={visibleData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#003B00" />
                <XAxis
                  dataKey="time"
                  type="number"
                  domain={['dataMin', chartData[chartData.length - 1]?.time || 'dataMax']}
                  tickFormatter={(t) => fmtTime(new Date(t).toISOString())}
                  tick={{ fill: '#00FF41', fontSize: 10 }}
                  stroke="#003B00"
                />
                <YAxis
                  domain={[priceRange.min * 0.998, priceRange.max * 1.002]}
                  tick={{ fill: '#00FF41', fontSize: 10 }}
                  stroke="#003B00"
                  tickFormatter={(v) => (v < 0.01 ? v.toFixed(6) : v.toFixed(0))}
                  width={70}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: 10, color: '#00FF41' }} />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="#00FF41"
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                  name="Price"
                />
                {/* Trade dots — one per candle that had a trade */}
                {visibleData.map((d, i) => {
                  const dots = [];
                  if (d.buy !== undefined) {
                    dots.push(
                      <ReferenceDot key={`b-${i}`} x={d.time} y={d.buy} r={5} fill="#22c55e" stroke="#fff" strokeWidth={1} />
                    );
                  }
                  if (d.sell !== undefined) {
                    dots.push(
                      <ReferenceDot key={`s-${i}`} x={d.time} y={d.sell} r={5} fill="#ef4444" stroke="#fff" strokeWidth={1} />
                    );
                  }
                  return dots;
                })}
                {/* Playhead line */}
                {currentPoint && (
                  <ReferenceLine x={currentPoint.time} stroke="#00FF41" strokeDasharray="3 3" strokeOpacity={0.7} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Scrubber */}
      <Card className="matrix-card rounded-none mb-4">
        <CardContent className="p-3 md:p-4 space-y-3">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest opacity-70">
            <span data-testid="timeline-start-ts">{chartData[0] ? fmtTime(chartData[0].timestamp) : '—'}</span>
            <span data-testid="timeline-current-ts">{currentPoint ? fmtTime(currentPoint.timestamp) : '—'}</span>
            <span data-testid="timeline-end-ts">
              {chartData[chartData.length - 1] ? fmtTime(chartData[chartData.length - 1].timestamp) : '—'}
            </span>
          </div>
          <Slider
            value={[playheadIdx]}
            min={0}
            max={Math.max(0, chartData.length - 1)}
            step={1}
            onValueChange={(v) => {
              setPlaying(false);
              setPlayheadIdx(v[0]);
            }}
            data-testid="timeline-slider"
          />
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPlaying(false);
                setPlayheadIdx(0);
              }}
              className="border-[#003B00] hover:border-[#00FF41]"
              data-testid="timeline-btn-rewind"
            >
              <SkipBack className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPlaying((p) => !p)}
              className="border-[#003B00] hover:border-[#00FF41] min-w-[80px]"
              data-testid="timeline-btn-play"
            >
              {playing ? (
                <>
                  <Pause className="h-4 w-4 mr-1" strokeWidth={1.5} /> Pause
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-1" strokeWidth={1.5} /> Play
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPlaying(false);
                setPlayheadIdx(Math.max(0, chartData.length - 1));
              }}
              className="border-[#003B00] hover:border-[#00FF41]"
              data-testid="timeline-btn-forward"
            >
              <SkipForward className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Trades revealed */}
      <Card className="matrix-card rounded-none">
        <CardHeader className="border-b border-[#003B00] p-3 md:p-4">
          <CardTitle className="text-xs uppercase tracking-widest">
            TRADES REVEALED · {revealedTrades.length} / {data.trades?.length || 0}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 max-h-[300px] overflow-y-auto">
          {revealedTrades.length === 0 ? (
            <div className="p-6 text-xs opacity-60 text-center" data-testid="timeline-trades-empty">
              No trades in view yet — scrub forward or Play to reveal them.
            </div>
          ) : (
            <ul className="divide-y divide-[#003B00] text-xs" data-testid="timeline-trades-list">
              {[...revealedTrades].reverse().map((t, idx) => (
                <li
                  key={`${t.order_id || 't'}-${idx}`}
                  className="flex items-center gap-3 px-3 py-2"
                  data-testid={`timeline-trade-row-${idx}`}
                >
                  {t.type === 'BUY' ? (
                    <TrendingUp className="h-4 w-4 text-green-400 flex-shrink-0" strokeWidth={1.5} />
                  ) : (
                    <TrendingDown className="h-4 w-4 text-red-400 flex-shrink-0" strokeWidth={1.5} />
                  )}
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      t.type === 'BUY' ? 'border-green-500 text-green-400' : 'border-red-500 text-red-400'
                    }`}
                  >
                    {t.type}
                  </Badge>
                  <span className="opacity-60 min-w-[110px]">{fmtTime(t.timestamp)}</span>
                  <span className="flex-1">
                    {t.amount.toFixed(8)} @ {fmtPrice(t.price)}
                  </span>
                  {t.trigger && (
                    <Badge variant="outline" className="text-[9px] border-[#003B00] opacity-70">
                      {t.trigger}
                    </Badge>
                  )}
                  {t.type === 'SELL' && (
                    <span className={t.profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                      ${t.profit.toFixed(2)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
